/**
 * «Сотрудники ↔ Bitrix»: соответствие «сотрудник CRM ↔ учётка Bitrix»
 * (`/api/bitrix/user-links`). По нему «Распределение» выдаёт лиды. Спека
 * `docs/specs/2026-09-24-lead-distribution-design.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { type Links, loadLinks, resetLink, setLink } from "@/lib/distribution";

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle";
const input = "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] text-fg";

function LinksBlock() {
  const qc = useQueryClient();
  const [failure, setFailure] = useState("");
  const [filter, setFilter] = useState("");
  const q = useQuery({ queryKey: ["bitrix-links"], queryFn: loadLinks });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["bitrix-links"] });
    void qc.invalidateQueries({ queryKey: ["distribution"] });
  };
  const m = useMutation({
    mutationFn: ({ crm, value }: { crm: number; value: string }) =>
      value === "crm" ? resetLink(crm) : setLink(crm, value === "none" ? null : Number(value)),
    onSuccess: () => {
      setFailure("");
      refresh();
    },
    onError: (e: Error) => setFailure(e.message),
  });
  if (q.isPending) return <div className="text-[12.5px] text-fg-muted">…</div>;
  if (q.isError) return <div className="text-[12.5px] text-err">Не удалось получить соответствия: {q.error.message}</div>;
  const l: Links = q.data;
  const names = new Map(l.bitrix_users.map((b) => [b.id, b.name]));
  const users = l.users.filter((u) => u.name.toLowerCase().includes(filter.toLowerCase()));
  return (
    <section className={card} data-annot="соответствия">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">Сотрудник CRM ↔ учётка Bitrix</h2>
        <span className="text-[12px] text-fg-faint">ручная связь сильнее sUser.b24UserID</span>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="поиск"
          className={`${input} ml-auto w-48`}
        />
      </div>
      {failure && <div className="px-4 py-2 text-[12.5px] text-warn">{failure}</div>}
      <table className="w-full text-[12.5px]">
        <tbody>
          {users.map((u) => (
            <tr key={u.crm_user_id} className="border-t border-border first:border-t-0 hover:bg-bg-hover" data-annot={`сотрудник ${u.crm_user_id}`}>
              <td className="px-4 py-1.5">
                {u.name}
                <span className="ml-2 font-mono text-[11px] text-fg-faint">{u.crm_user_id}</span>
              </td>
              <td className="px-4 py-1.5">
                <select
                  value={u.source === "manual" ? (u.bitrix_user_id ?? "none") : "crm"}
                  disabled={m.isPending}
                  onChange={(e) => m.mutate({ crm: u.crm_user_id, value: e.target.value })}
                  className={`${input} w-72`}
                  aria-label={`Учётка Bitrix: ${u.name}`}
                >
                  <option value="crm">
                    из CRM: {u.source === "crm" && u.bitrix_user_id ? names.get(u.bitrix_user_id) ?? `#${u.bitrix_user_id}` : "—"}
                  </option>
                  <option value="none">вручную: нет учётки</option>
                  {l.bitrix_users.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                      {b.active ? "" : " (уволен)"} · {b.id}
                    </option>
                  ))}
                </select>
              </td>
              <td className="px-4 py-1.5 text-[11.5px] text-fg-faint">
                {u.source === "manual" ? "вручную" : u.source === "crm" ? "из CRM" : "нет связи"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export function BitrixUsersPage() {
  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5 [&>*]:flex-none">
      <h1 className="text-[16px] font-semibold tracking-[-0.015em]" data-annot="шапка сотрудники bitrix">
        Сотрудники ↔ Bitrix
      </h1>
      <LinksBlock />
    </main>
  );
}
